/**
 * Mobile Sidebar Management
 *
 * Handles the mobile sidebar toggle, overlay, and gesture support.
 *
 * @module components/mobile-sidebar
 */

// Track touch positions for swipe gestures
let touchStartX = 0;
let touchEndX = 0;

/**
 * Get sidebar elements.
 * @returns {Object} Object containing sidebar, overlay, and body elements
 */
function getElements() {
  return {
    sidebar: document.getElementById('sidebar'),
    overlay: document.querySelector('.sidebar-overlay'),
    body: document.body,
  };
}

/**
 * Check if we're on mobile (viewport width <= 768px).
 * @returns {boolean}
 */
function isMobile() {
  return window.innerWidth <= 768;
}

/**
 * Toggle the mobile sidebar open/closed.
 */
export function toggleMobileSidebar() {
  const { sidebar, overlay, body } = getElements();

  if (sidebar && overlay) {
    const isOpening = !sidebar.classList.contains('active');

    sidebar.classList.toggle('active');
    overlay.classList.toggle('active');

    // Prevent body scroll when sidebar is open
    if (isOpening) {
      body.classList.add('sidebar-open');
    } else {
      body.classList.remove('sidebar-open');
    }
  }
}

/**
 * Close the mobile sidebar (used on navigation).
 */
export function closeMobileSidebarOnNavigate() {
  // Only close on mobile
  if (!isMobile()) return;

  const { sidebar, overlay, body } = getElements();

  if (sidebar && overlay) {
    sidebar.classList.remove('active');
    overlay.classList.remove('active');
    body.classList.remove('sidebar-open');
  }
}

/**
 * Handle window resize - close sidebar when moving from mobile to desktop.
 */
function handleResize() {
  if (!isMobile()) {
    const { sidebar, overlay, body } = getElements();

    if (sidebar && overlay) {
      sidebar.classList.remove('active');
      overlay.classList.remove('active');
      body.classList.remove('sidebar-open');
    }
  }
}

/**
 * Handle ESC key to close sidebar.
 * @param {KeyboardEvent} e
 */
function handleEscKey(e) {
  if (e.key === 'Escape' && isMobile()) {
    const { sidebar } = getElements();
    if (sidebar && sidebar.classList.contains('active')) {
      toggleMobileSidebar();
    }
  }
}

/**
 * Handle touch start for swipe gestures.
 * @param {TouchEvent} e
 */
function handleTouchStart(e) {
  touchStartX = e.changedTouches[0].screenX;
}

/**
 * Handle touch end for swipe gestures.
 * @param {TouchEvent} e
 */
function handleTouchEnd(e) {
  touchEndX = e.changedTouches[0].screenX;
  handleSwipeGesture();
}

/**
 * Process swipe gesture to open/close sidebar.
 */
function handleSwipeGesture() {
  const swipeThreshold = 50;
  const { sidebar } = getElements();

  if (!sidebar) return;

  // Swipe right to open (from left edge)
  if (touchStartX < 50 && touchEndX > touchStartX + swipeThreshold) {
    if (!sidebar.classList.contains('active')) {
      toggleMobileSidebar();
    }
  }

  // Swipe left to close
  if (sidebar.classList.contains('active') && touchStartX > touchEndX + swipeThreshold) {
    toggleMobileSidebar();
  }
}

/**
 * Initialize mobile sidebar functionality.
 */
export function initMobileSidebar() {
  // Debounced resize handler
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(handleResize, 250);
  });

  // ESC key handler
  document.addEventListener('keydown', handleEscKey);

  // Swipe gesture support
  document.addEventListener('touchstart', handleTouchStart, false);
  document.addEventListener('touchend', handleTouchEnd, false);

  // Expose functions globally for onclick handlers in templates
  window.toggleMobileSidebar = toggleMobileSidebar;
  window.closeMobileSidebarOnNavigate = closeMobileSidebarOnNavigate;
}

// Auto-initialize on DOM ready
document.addEventListener('DOMContentLoaded', initMobileSidebar);

// Export for module usage
export default {
  toggleMobileSidebar,
  closeMobileSidebarOnNavigate,
  initMobileSidebar,
};
